import { z } from "zod";
import { MediumText, EscalationSeverity, EscalationLevel, EscalationTone, idString, } from "./common.js";
/**
 * PB-4923 added `id`, optional only so pre-backfill rows still validate.
 *
 * `(severity, level)` is the cell's COORDINATE and is required to be unique
 * (see the refinement below), but it is not the identity. Two reasons. Moving a
 * cell to another coordinate is one gesture that should keep the cell — with a
 * derived identity it reads as a delete plus an add, and either half can be
 * skipped independently. And a removal leaves a tombstone under its key, so a
 * coordinate-derived identity would make "clear this cell, then fill it in
 * again" permanently impossible. With a minted id the coordinate is ordinary
 * content: moving a cell is a field edit that keeps its revision, and a
 * re-created cell is simply a new cell.
 */
const EscalationCell = z
    .object({
    id: idString.optional(),
    severity: EscalationSeverity,
    level: EscalationLevel,
    label: MediumText.optional(),
    tone: EscalationTone.optional(),
})
    .strict();
/**
 * Escalation Matrix — Ensure, singleton. Sparse 3×3 grid (severity rows ×
 * level columns). Only filled cells are persisted; missing cells render
 * as neutral placeholders.
 */
export const EscalationMatrixParamsSchema = z
    .object({
    cells: z.array(EscalationCell).max(9),
})
    .strict()
    .superRefine((params, ctx) => {
    /* PB-4923. `(severity, level)` IS this widget's item identity — it is a
       sparse 3×3 grid and both consumers read it that way — and the plan
       requires that identity to be unique. Nothing enforced it: nine copies of
       ('High','Owner') fit inside `.max(9)`, and `.find()` silently returned
       the first, so a second cell for one coordinate was invisible content that
       no editor could reach.

       Enforced here rather than only at operation time because a duplicate
       makes the identity itself meaningless — an operation addressing
       `cells[High|Owner]` cannot say which row it means, and neither can a
       reader. The executor still refuses an ambiguous operation independently;
       this stops one being created. */
    const seen = new Set();
    params.cells.forEach((cell, index) => {
        const key = `${cell.severity}|${cell.level}`;
        if (seen.has(key)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["cells", index],
                message: `Duplicate cell for ${cell.severity}/${cell.level}; each severity and level pair may appear once.`,
            });
        }
        seen.add(key);
    });
});
export const defaultEscalationMatrixParams = {
    cells: [],
};
/** The published params shape before PB-4923 added cell ids. */
export const RetiredEscalationMatrixParamsV1Schema = z
    .object({
    cells: z
        .array(z
        .object({
        severity: EscalationSeverity,
        level: EscalationLevel,
        label: MediumText.optional(),
        tone: EscalationTone.optional(),
    })
        .strict())
        .max(9),
})
    .strict();
//# sourceMappingURL=escalationMatrix.js.map